import type { AIAssessmentEvaluationDataType } from "api/api-server-ai";
import type { AIAssessmentResponseType } from "components/ai/queries/assessment-query";
import type { AIModelType, AssessmentType, SubmissionType } from "components/server-types";
import type {
  FullGroupSubmissionType,
  FullIndividualSubmissionType,
  NestedPageOutcomeType,
} from "store/selectors";
import type { CSVData } from "utils/file";
import { rollbarAndLogError } from "utils/logger";

export type HumanAssessedSubmission = {
  submission: SubmissionType;
  assessment: AssessmentType;
  outcomeScores: Record<string, number>;
  subjectType: "individual" | "group";
};

export type AIAssessmentComparisonRecord = {
  humanAssessedSubmission: HumanAssessedSubmission;
  aiResponse?: AIAssessmentResponseType;
  evaluationData?: AIAssessmentEvaluationDataType;
  errorMessage?: string;
  generatedAt: string;
  durationMs: number;
};

export type AIAssessmentEvaluationSubmission =
  | FullIndividualSubmissionType
  | FullGroupSubmissionType;

export const buildHumanAssessedSubmissions = ({
  submissions,
}: {
  submissions: AIAssessmentEvaluationSubmission[];
}): HumanAssessedSubmission[] => {
  return submissions.flatMap((submission): HumanAssessedSubmission[] => {
    if (!submission.assessment) return [];

    return [
      {
        submission,
        assessment: submission.assessment,
        outcomeScores: Object.fromEntries(
          (submission.outcome_assessments ?? []).map((outcome) => [
            outcome.page_outcome_id,
            outcome.score,
          ]),
        ),
        subjectType: "section_user" in submission ? "individual" : "group",
      },
    ];
  });
};

type RunEvaluationQueueOptions<TResult> = {
  humanAssessedSubmissions: HumanAssessedSubmission[];
  evaluateSubmission: (humanAssessedSubmission: HumanAssessedSubmission) => Promise<TResult>;
  signal: AbortSignal;
  onEvaluationSettled: (settled: {
    humanAssessedSubmission: HumanAssessedSubmission;
    result?: TResult;
    errorMessage?: string;
  }) => void;
  concurrency?: number;
};

export const runAIAssessmentEvaluationQueue = async <TResult>({
  humanAssessedSubmissions,
  evaluateSubmission,
  signal,
  onEvaluationSettled,
  concurrency = 2,
}: RunEvaluationQueueOptions<TResult>): Promise<void> => {
  let nextIndex = 0;
  const runNext = async (): Promise<void> => {
    if (signal.aborted) return;
    const index = nextIndex;
    nextIndex += 1;
    if (index >= humanAssessedSubmissions.length) return;
    const humanAssessedSubmission = humanAssessedSubmissions[index];
    try {
      const result = await evaluateSubmission(humanAssessedSubmission);
      if (!signal.aborted) onEvaluationSettled({ humanAssessedSubmission, result });
    } catch (error) {
      rollbarAndLogError("runAIAssessmentEvaluationQueue: evaluation failed.", error);
      if (!signal.aborted) {
        onEvaluationSettled({
          humanAssessedSubmission,
          errorMessage: error instanceof Error ? error.message : "Evaluation request failed",
        });
      }
    }
    return runNext();
  };

  await Promise.all(
    Array.from({ length: Math.min(concurrency, humanAssessedSubmissions.length) }, () => runNext()),
  );
};

const neutralizeSpreadsheetFormula = (value: string): string =>
  /^[=+\-@]/.test(value.trimStart()) ? `'${value}` : value;

export const buildAIAssessmentComparisonCsv = (
  comparisonRecords: AIAssessmentComparisonRecord[],
  reportMetadata: {
    sectionId: string;
    sectionTitle: string;
    pageId: string;
    pageTitle: string;
    model: AIModelType;
  },
  pageOutcomes: NestedPageOutcomeType[],
): CSVData => {
  const headers = [
    "section_id",
    "section_title",
    "page_id",
    "page_title",
    "submission_id",
    "worksheet_version",
    "page_instructions",
    "student_response",
    "rubric_context",
    "submission_files",
    "subject_type",
    "human_assessed_at",
    "human_points",
    "human_comment",
    ...pageOutcomes.flatMap((outcome) => [
      `human_score_#${outcome.outcome.title}_${outcome.id}`,
      `ai_score_#${outcome.outcome.title}_${outcome.id}`,
    ]),
    "ai_feedback",
    "ai_refused",
    "status",
    "error",
    "model",
    "feedback_style_applied",
    "feedback_style",
    "generated_at",
    "duration_ms",
  ];

  const rows = comparisonRecords.map((comparisonRecord) => {
    const { humanAssessedSubmission, aiResponse, evaluationData } = comparisonRecord;
    const errorMessage = comparisonRecord.errorMessage ?? aiResponse?.errorMessage;
    const aiScores = Object.fromEntries(
      (aiResponse?.outcomes ?? []).map((outcome) => [outcome.pageOutcomeId, outcome.score]),
    );
    return [
      reportMetadata.sectionId,
      neutralizeSpreadsheetFormula(reportMetadata.sectionTitle),
      reportMetadata.pageId,
      neutralizeSpreadsheetFormula(reportMetadata.pageTitle),
      humanAssessedSubmission.submission.id,
      evaluationData?.worksheet_version ?? "",
      neutralizeSpreadsheetFormula(evaluationData?.page_instructions ?? ""),
      neutralizeSpreadsheetFormula(evaluationData?.student_response ?? ""),
      neutralizeSpreadsheetFormula(evaluationData?.rubric_context ?? ""),
      evaluationData
        ? neutralizeSpreadsheetFormula(JSON.stringify(evaluationData.submission_files))
        : "",
      humanAssessedSubmission.subjectType,
      humanAssessedSubmission.assessment.created_at,
      humanAssessedSubmission.assessment.points ?? "",
      neutralizeSpreadsheetFormula(humanAssessedSubmission.assessment.comment ?? ""),
      ...pageOutcomes.flatMap((outcome) => [
        humanAssessedSubmission.outcomeScores[outcome.id] ?? "",
        aiScores[outcome.id] ?? "",
      ]),
      neutralizeSpreadsheetFormula(aiResponse?.feedback ?? ""),
      neutralizeSpreadsheetFormula(aiResponse?.refused ?? ""),
      errorMessage ? "error" : aiResponse?.refused ? "refused" : "success",
      neutralizeSpreadsheetFormula(errorMessage ?? ""),
      reportMetadata.model,
      evaluationData ? (evaluationData.feedback_style_applied ? "yes" : "no") : "",
      neutralizeSpreadsheetFormula(evaluationData?.feedback_style ?? ""),
      comparisonRecord.generatedAt,
      comparisonRecord.durationMs,
    ];
  });

  return { headers, rows };
};
